import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Phone,
  Email,
  Person,
  CheckCircle,
  Cancel,
  PlayArrow,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetContactsByTagQuery, useCreateCampaignMutation } from '../store/api/outboundCallingApi';
import { useGetActiveSynthflowAccountsQuery } from '../store/api/synthflowApi';
import { useGetAgentsForAccountQuery } from '../store/api/synthflowAgentsApi';

interface CampaignPreviewData {
  campaign_name: string;
  tag_name: string;
  agent_id: string;
  umbrella_account_id: string;
  interval_minutes: number;
  timezone: string;
  start_hour: number;
  end_hour: number;
  max_call_duration_minutes: number;
  max_retries: number;
  days_of_week: string[];
}

const CampaignPreviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [campaignData, setCampaignData] = useState<CampaignPreviewData | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Get agent_id and account_id from URL parameters
  const agentId = searchParams.get('agent_id');
  const accountId = searchParams.get('account_id');

  // Parse URL parameters
  useEffect(() => {
    const data: CampaignPreviewData = {
      campaign_name: searchParams.get('campaign_name') || '',
      tag_name: searchParams.get('tag_name') || '',
      agent_id: searchParams.get('agent_id') || '',
      umbrella_account_id: searchParams.get('umbrella_account_id') || '',
      interval_minutes: parseInt(searchParams.get('interval_minutes') || '10'),
      timezone: searchParams.get('timezone') || 'America/New_York',
      start_hour: parseInt(searchParams.get('start_hour') || '9'),
      end_hour: parseInt(searchParams.get('end_hour') || '17'),
      max_call_duration_minutes: parseInt(searchParams.get('max_call_duration_minutes') || '5'),
      max_retries: parseInt(searchParams.get('max_retries') || '3'),
      days_of_week: (searchParams.get('days_of_week') || '').split(',').filter(Boolean),
    };
    console.log('Parsed campaign data from URL:', data);
    console.log('URL search params:', Object.fromEntries(searchParams.entries()));
    setCampaignData(data);
  }, [searchParams]);

  // Get contacts for the selected tag
  const { data: contacts, isLoading: contactsLoading, error: contactsError } = useGetContactsByTagQuery(
    campaignData?.tag_name || '',
    { skip: !campaignData?.tag_name }
  );

  // Create campaign mutation
  const [createCampaign] = useCreateCampaignMutation();

  // Get accounts and agents for breadcrumb
  const { data: accounts = [] } = useGetActiveSynthflowAccountsQuery();
  const { data: agents } = useGetAgentsForAccountQuery(accountId ? parseInt(accountId) : 1, {
    skip: !accountId && !agentId
  });

  const handleCreateCampaign = async () => {
    if (!campaignData) return;

    setIsCreating(true);
    try {
      const campaignPayload = {
        name: campaignData.campaign_name,
        tag_name: campaignData.tag_name,
        agent_id: parseInt(campaignData.agent_id),
        umbrella_account_id: parseInt(campaignData.umbrella_account_id),
        interval_minutes: campaignData.interval_minutes,
        timezone: campaignData.timezone,
        start_hour: campaignData.start_hour,
        end_hour: campaignData.end_hour,
        max_call_duration_minutes: campaignData.max_call_duration_minutes,
        max_retries: campaignData.max_retries,
        days_of_week: campaignData.days_of_week,
      };
      
      console.log('Creating campaign with payload:', campaignPayload);
      await createCampaign(campaignPayload).unwrap();

      // Navigate to campaigns list
      navigate('/outbound-calling');
    } catch (error: any) {
      console.error('Error creating campaign:', error);
      console.error('Error details:', error.data);
      console.error('Error status:', error.status);
      
      // Handle specific error cases
      if (error.data?.detail?.includes('already has an active or paused campaign')) {
        alert('This agent already has an active or paused campaign. Please stop or complete the existing campaign before creating a new one.');
      } else {
        alert(`Failed to create campaign: ${error.data?.detail || error.message || 'Unknown error'}`);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const formatDaysOfWeek = (days: string[]) => {
    return days.map(day => 
      day.charAt(0).toUpperCase() + day.slice(1)
    ).join(', ');
  };

  if (!campaignData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {/* Breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link color="inherit" onClick={() => navigate('/aigent/agents')} sx={{ cursor: 'pointer' }}>
            {accounts.find((acc: any) => acc.id === parseInt(accountId || '1'))?.name || 'Account'}
          </Link>
          {agentId && agents && (
            <Link color="inherit" onClick={() => navigate('/aigent/agents')} sx={{ cursor: 'pointer' }}>
              {agents.find((agent: any) => agent.id === parseInt(agentId))?.name || `Agent ${agentId}`}
            </Link>
          )}
          <Link 
            color="inherit" 
            onClick={() => {
              const params = new URLSearchParams();
              if (agentId) params.set('agent_id', agentId);
              if (accountId) params.set('account_id', accountId);
              navigate(`/outbound-calling?${params.toString()}`);
            }} 
            sx={{ cursor: 'pointer' }}
          >
            Campaigns
          </Link>
          <Link 
            color="inherit" 
            onClick={() => {
              const params = new URLSearchParams();
              if (agentId) params.set('agent_id', agentId);
              if (accountId) params.set('account_id', accountId);
              navigate(`/outbound-calling/create?${params.toString()}`);
            }} 
            sx={{ cursor: 'pointer' }}
          >
            Create Campaign
          </Link>
          <Typography color="text.primary">Preview Contacts</Typography>
        </Breadcrumbs>
      </Box>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        
        <Typography variant="h4" gutterBottom>
          Campaign Preview
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review your campaign settings and contacts before starting.
        </Typography>
      </Box>

      {/* Campaign Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Campaign Settings
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Campaign Name
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {campaignData.campaign_name}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Tag
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {campaignData.tag_name}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Agent ID
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {campaignData.agent_id}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              Account ID
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {campaignData.umbrella_account_id}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Interval
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {campaignData.interval_minutes} minutes
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Timezone
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {campaignData.timezone}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Calling Hours
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {campaignData.start_hour}:00 - {campaignData.end_hour}:00
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Max Call Duration
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {campaignData.max_call_duration_minutes} minutes
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Max Retries
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {campaignData.max_retries}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Calling Days
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {formatDaysOfWeek(campaignData.days_of_week)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Contacts Preview */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Contacts ({contacts?.length || 0})
        </Typography>
        
        {contactsLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : contactsError ? (
          <Alert severity="error">
            Failed to load contacts. Please try again.
          </Alert>
        ) : contacts && contacts.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Tag</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person fontSize="small" />
                        {contact.first_name} {contact.last_name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email fontSize="small" />
                        {contact.email}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Phone fontSize="small" />
                        {contact.phone}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={contact.tag_name} size="small" color="primary" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="warning">
            No contacts found for the selected tag.
          </Alert>
        )}
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={() => {
            const agentId = searchParams.get('agent_id');
            const accountId = searchParams.get('account_id');
            const params = new URLSearchParams();
            if (agentId) params.set('agent_id', agentId);
            if (accountId) params.set('account_id', accountId);
            navigate(`/outbound-calling/create?${params.toString()}`);
          }}
          disabled={isCreating}
        >
          Back to Form
        </Button>
        <Button
          variant="contained"
          onClick={handleCreateCampaign}
          disabled={!contacts || contacts.length === 0 || isCreating}
          startIcon={isCreating ? <CircularProgress size={20} /> : <PlayArrow />}
        >
          {isCreating ? 'Creating Campaign...' : 'Start Campaign'}
        </Button>
      </Box>
    </Container>
  );
};

export default CampaignPreviewPage;
